import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import {
  LineItemRecurrence,
  ShoppingRecurrence,
  ShoppingRecurrenceCycle,
  ShoppingRecurrenceEdition,
  ShoppingRecurrenceEditionStatus,
  ShoppingRecurrenceStatus,
  Shops
} from '@infrab4a/connect';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { PersonControllerService, PersonDetail } from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { ShopShoppingRecurrenceEditionService } from 'src/app/connect-api/api/shop/shop-shopping-recurrence-edition.service';
import { ShopShoppingRecurrenceService } from 'src/app/connect-api/api/shop/shop-shopping-recurrence.service';
import { getShopLabel } from 'src/app/connect-api/enums/ShopMap';
import { PaymentMethod } from 'src/app/connect-api/models/PaymentMethod';
import {
  getAllRecurrenceCycles,
  getEditionStatusLabel,
  getRecurrenceCycleLabel,
  getRecurrenceStatusLabel
} from 'src/app/connect-api/models/ShoppingRecurrenceLabels';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-shop-recurrence-detail',
  templateUrl: './shop-recurrence-detail.component.html',
  styleUrl: './shop-recurrence-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [ConfirmationService, MessageService, DatePipe, CurrencyPipe]
})
export class ShopRecurrenceDetailComponent implements OnInit {
  private title = inject(Title);
  private recurrenceService = inject(ShopShoppingRecurrenceService);
  private editionService = inject(ShopShoppingRecurrenceEditionService);
  private personService = inject(PersonControllerService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);

  @Input() recurrenceId: string;

  protected recurrence: ShoppingRecurrence;
  protected editions: ShoppingRecurrenceEdition[] = [];
  protected person: PersonDetail;
  protected selectedTab = 0;
  protected tableReady = false;
  protected cycleOptions = getAllRecurrenceCycles();
  protected cycleControl = new FormControl<ShoppingRecurrenceCycle>(null);

  protected productCols = [
    new TableColumn.Builder()
      .setHeader('Imagem')
      .setField('image')
      .setType('image')
      .setFilter(false)
      .setStyleClass('product-image')
      .setDisplayFunction((item: LineItemRecurrence) =>
        this.getLineItemImage(item)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Admin')
      .setField('id')
      .setRouterLink('/shop-products/catalog/product/')
      .setRouterLinkFieldName('id')
      .setBusinessUnit(() =>
        this.recurrence?.shop === Shops.MENSMARKET ? 2 : 1
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('EAN')
      .setRouterLink('/shop-products/catalog/product/')
      .setRouterLinkFieldName('id')
      .setBusinessUnit(() =>
        this.recurrence?.shop === Shops.MENSMARKET ? 2 : 1
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('name')
      .setRouterLink('/shop-products/catalog/product/')
      .setRouterLinkFieldName('id')
      .setBusinessUnit(() =>
        this.recurrence?.shop === Shops.MENSMARKET ? 2 : 1
      )
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Marca')
      .setField('brand')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Qtd')
      .setField('quantity')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Preço item')
      .setField('pricePaid')
      .setDisplayFunction((item: LineItemRecurrence) => item.pricePaid)
      .setType('currency')
      .setCondition('gte')
      .build()
  ];

  protected editionCols = [
    new TableColumn.Builder()
      .setHeader('Data agendada')
      .setField('scheduledDate')
      .setType('date')
      .setDisplayFunction((edition: ShoppingRecurrenceEdition) =>
        this.datePipe.transform(edition.scheduledDate, 'dd/MM/yyyy HH:mm')
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setDisplayFunction((edition: ShoppingRecurrenceEdition) =>
        getEditionStatusLabel(edition.status)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Pedido')
      .setField('orderId')
      .setRouterLink('/shop-orders/order/')
      .setRouterLinkFieldName('orderId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pagamento')
      .setField('paymentId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Processado em')
      .setField('processedAt')
      .setType('date')
      .setDisplayFunction((edition: ShoppingRecurrenceEdition) =>
        edition.processedAt
          ? this.datePipe.transform(edition.processedAt, 'dd/MM/yyyy HH:mm')
          : null
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Erro')
      .setField('errorMessage')
      .setDisplayFunction((edition: ShoppingRecurrenceEdition) =>
        edition.errorMessage || edition.errorCode
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Tentativas')
      .setField('failedAttempts')
      .setType('formattedInteger')
      .build(),
    new TableColumn('Ação', '', false, 'button')
  ];

  protected historyCols = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('date')
      .setType('date')
      .setDisplayFunction((item: ShoppingRecurrence['history'][0]) =>
        this.datePipe.transform(item.date, 'dd/MM/yyyy HH:mm')
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setDisplayFunction((item: ShoppingRecurrence['history'][0]) =>
        getRecurrenceStatusLabel(item.status)
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Ativo')
      .setField('active')
      .setType('boolean')
      .build(),
    new TableColumn.Builder()
      .setHeader('Descrição')
      .setField('description')
      .build()
  ];

  async ngOnInit() {
    if (!this.recurrenceId) {
      AppDialogService.showErrorDialog(
        { message: 'Recorrência não encontrada.' },
        true
      );
      return;
    }
    this.recurrenceId = this.recurrenceId.trim();
    await this.loadRecurrence();
  }

  private async loadRecurrence() {
    LoaderService.showLoader();
    try {
      await this.reloadRecurrenceData({ reloadPerson: true });
      this.title.setTitle(`Recorrência ${this.recurrence.id} - Loja`);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async reloadRecurrenceData(options?: { reloadPerson?: boolean }) {
    this.tableReady = false;
    this.recurrence = await this.recurrenceService.getById(this.recurrenceId);
    this.cycleControl.setValue(this.recurrence.recurrenceCycle);
    const editionsResult =
      await this.editionService.getByRecurrenceId(this.recurrenceId);
    this.editions = editionsResult.data ?? [];
    if (options?.reloadPerson) {
      await this.loadPersonalData();
    }
    this.tableReady = true;
  }

  private async loadPersonalData() {
    if (
      ['Glamshop', 'Glampoints'].includes(this.recurrence?.shop) &&
      this.recurrence.user?.id
    ) {
      this.person = await lastValueFrom(
        this.personService
          .findPersonInfoById(Number(this.recurrence.user.id))
          .pipe(map((data) => data.result))
      );
    } else if (this.recurrence?.user) {
      this.person = {
        personId: 0,
        username: this.recurrence.user.email,
        name:
          this.recurrence.user.firstName ||
          this.recurrence.user.displayName,
        lastName: this.recurrence.user.lastName,
        phone: FormUtil.phoneFormatted(Number(this.recurrence.user.phone)),
        birthday: this.recurrence.user.birthday?.toISOString() as any,
        cpf: Number(this.recurrence.user.cpf),
        urlImageProfile: this.recurrence.user.urlImageProfile
      };
    }
  }

  tabChanged(index: number) {
    this.selectedTab = index;
  }

  get shopName(): string {
    return getShopLabel(this.recurrence?.shop);
  }

  get methodName(): string {
    return PaymentMethod[this.recurrence?.paymentMethod];
  }

  get canToggleActive(): boolean {
    return this.recurrence?.status !== ShoppingRecurrenceStatus.CANCELLED;
  }

  get canEditFrequency(): boolean {
    return this.recurrence?.status !== ShoppingRecurrenceStatus.CANCELLED;
  }

  get statusLabel(): string {
    return getRecurrenceStatusLabel(this.recurrence?.status);
  }

  get activeLabel(): string {
    return this.recurrence?.active ? 'Sim' : 'Não';
  }

  get cycleLabel(): string {
    return getRecurrenceCycleLabel(this.recurrence?.recurrenceCycle);
  }

  private getLineItemImage(item: LineItemRecurrence): string | undefined {
    const lineItem = item as LineItemRecurrence & { image?: string };

    return (
      lineItem.image || item.images?.[0] || item.miniatures?.[0] || undefined
    );
  }

  askUpdateFrequency = () => {
    const frequency = this.cycleControl.value;
    const frequencyLabel = getRecurrenceCycleLabel(frequency);
    this.confirmationService.confirm({
      header: 'Alterar ciclo',
      message: `Deseja alterar o ciclo da recorrência para "${frequencyLabel}"?`,
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      acceptLabel: 'Alterar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => this.updateFrequency(frequency)
    });
  };

  askToggleActive = () => {
    const newActive = !this.recurrence.active;
    this.confirmationService.confirm({
      header: newActive ? 'Ativar recorrência' : 'Desativar recorrência',
      message: `Tem certeza que deseja ${newActive ? 'ativar' : 'desativar'} a recorrência "${this.recurrence.id}"?`,
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      acceptLabel: newActive ? 'Ativar' : 'Desativar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: newActive
        ? 'p-button-success'
        : 'p-button-warning',
      rejectButtonStyleClass: 'p-button-primary',
      accept: () => this.toggleActive(newActive)
    });
  };

  askSkipEdition = (
    edition: ShoppingRecurrenceEdition,
    $event?: MouseEvent
  ) => {
    this.confirmationService.confirm({
      header: 'Pular edição',
      message: `Deseja pular a edição agendada para ${this.datePipe.transform(edition.scheduledDate, 'dd/MM/yyyy HH:mm')}?`,
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      acceptLabel: 'Pular',
      rejectLabel: 'Manter',
      acceptButtonStyleClass: 'p-button-warning',
      rejectButtonStyleClass: 'p-button-primary',
      ...($event?.target && { target: $event.target }),
      accept: () => this.skipEdition(edition)
    });
  };

  protected editionActionButtons = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.FORWARD)
      .setTooltip('Pular edição')
      .setTooltipPosition('bottom')
      .setSeverity('warning')
      .setSize('small')
      .setText(true)
      .setAction(this.askSkipEdition)
      .setDisplay(
        (edition: ShoppingRecurrenceEdition) =>
          edition.status === ShoppingRecurrenceEditionStatus.SCHEDULED
      )
      .build()
  ];

  private async toggleActive(active: boolean) {
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.recurrenceService.enableOrDisableShoppingRecurrence(
          this.recurrence.id,
          active
        )
      );
      await this.reloadRecurrenceData();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `Recorrência ${active ? 'ativada' : 'desativada'} com sucesso`
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async updateFrequency(frequency: ShoppingRecurrenceCycle) {
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.recurrenceService.updateShoppingRecurrenceFrequency(
          this.recurrence.id,
          frequency
        )
      );
      await this.reloadRecurrenceData();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Ciclo alterado com sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async skipEdition(edition: ShoppingRecurrenceEdition) {
    LoaderService.showLoader();
    try {
      await lastValueFrom(
        this.editionService.skipShoppingRecurrenceEdition(edition.id)
      );
      await this.reloadRecurrenceData();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Edição pulada com sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
